import React, { useEffect, useState, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchAllRedesigns,
  updateRedesignStatus,
} from "../../store/admin/redesign";
import { fetchsearchByRedesignCaseIdAndStatus } from "../../store/common/searchSlice";
import { Helmet } from "react-helmet-async";
import {
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaInbox,
  FaCheck,
} from "react-icons/fa";

const ACCENT = "#2fa9bf";

const statusOptions = [
  { value: "", label: "All statuses" },
  { value: "to be downloaded", label: "To Be Downloaded" },
  { value: "downloaded", label: "Downloaded" },
  { value: "completed", label: "Completed" },
];

const redesignStatusStyles = (status: string) => {
  const s = (status || "").toLowerCase();
  if (s === "completed")
    return "bg-emerald-50 text-emerald-700 ring-emerald-200";
  if (s === "downloaded")
    return "bg-sky-50 text-sky-700 ring-sky-200";
  if (s === "to be downloaded")
    return "bg-amber-50 text-amber-800 ring-amber-200";
  return "bg-gray-50 text-gray-600 ring-gray-200";
};

const formatStatusLabel = (status: string) => {
  if (!status) return "—";
  const s = status.toLowerCase();
  if (s === "to be downloaded") return "To Be Downloaded";
  if (s === "downloaded") return "Downloaded";
  if (s === "completed") return "Completed";
  return status;
};

const formatCreation = (value: Date | string | number | null | undefined) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Redesigns = () => {
  const dispatch = useAppDispatch();

  const {
    allRedesigns,
    isLoading: redesignLoading,
    error: redesignError,
    totalPages,
  } = useAppSelector((state) => state.redesignCases);

  const {
    searchedRedesigns,
    loading: { searchedRedesigns: searchLoading },
    error: { searchedRedesigns: searchError },
  } = useAppSelector((state) => state.search);

  const { user } = useAppSelector((state) => state.auth);

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [updating, setUpdating] = useState<string | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    case_id: string;
    redesign_attempt: number | null;
  }>({ visible: false, case_id: "", redesign_attempt: null });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchTerm.trim()), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, status]);

  const isSearching = !!debouncedSearch || !!status;
  const hasActiveFilters = !!searchTerm.trim() || !!status;

  useEffect(() => {
    if (isSearching) {
      dispatch(
        fetchsearchByRedesignCaseIdAndStatus({
          caseId: debouncedSearch,
          status,
        }),
      );
    } else {
      dispatch(fetchAllRedesigns({ page, search: "", status: "" }));
    }
  }, [dispatch, page, debouncedSearch, status, isSearching]);

  const activeData = isSearching ? searchedRedesigns : allRedesigns;
  const isLoading = isSearching ? searchLoading : redesignLoading;
  const error = isSearching ? searchError : redesignError;

  const redesignData = useMemo(
    () =>
      Object.entries(activeData || {}).map(([case_id, redesigns]) => ({
        case_id,
        redesigns,
      })),
    [activeData],
  );

  const totalAttempts = useMemo(
    () => redesignData.reduce((sum, c) => sum + c.redesigns.length, 0),
    [redesignData],
  );

  const canAct = !["production", "admin read"].includes(
    user?.role?.toLowerCase() || "",
  );

  const colCount = canAct ? 7 : 6;

  const handlePrev = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => Math.min(totalPages || 1, p + 1));

  const clearFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setStatus("");
    setPage(1);
  };

  const openConfirmModal = (case_id: string, redesign_attempt: number) => {
    setConfirmModal({ visible: true, case_id, redesign_attempt });
  };

  const closeModal = () =>
    setConfirmModal({ visible: false, case_id: "", redesign_attempt: null });

  const handleConfirmDone = async () => {
    if (!confirmModal.case_id || confirmModal.redesign_attempt == null) return;

    const { case_id, redesign_attempt } = confirmModal;
    setUpdating(`${case_id}-${redesign_attempt}`);
    closeModal();

    try {
      await dispatch(
        updateRedesignStatus({
          case_id,
          status: "Completed",
          redesign_attempt,
        }),
      ).unwrap();

      if (isSearching) {
        dispatch(
          fetchsearchByRedesignCaseIdAndStatus({
            caseId: debouncedSearch,
            status,
          }),
        );
      } else {
        dispatch(fetchAllRedesigns({ page, search: "", status: "" }));
      }
    } catch (err) {
      console.error("Failed to update status:", err);
    } finally {
      setUpdating(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Redesign Cases</title>
      </Helmet>

      <div className="flex flex-col gap-4 w-full min-w-0 text-sm relative">
        {/* Header */}
        <div
          className="rounded-2xl px-4 py-4 md:px-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4"
          style={{ backgroundColor: ACCENT }}
        >
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-white truncate">
              Redesign Cases
            </h1>
            <p className="text-white/85 text-xs sm:text-sm mt-0.5">
              Track redesign attempts and mark them complete
            </p>
          </div>

          <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 w-full lg:w-auto min-w-0">
            <div className="relative w-full sm:w-[280px]">
              <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8A8A85] text-xs pointer-events-none" />
              <input
                type="text"
                placeholder="Search by Case ID…"
                className="w-full rounded-xl bg-white border border-white text-[#1A1A18] placeholder:text-[#8A8A85] pl-9 pr-9 py-2.5 outline-none shadow-sm focus:ring-2 focus:ring-white/70 transition"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm ? (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8A8A85] hover:text-[#4A4A48]"
                >
                  <FaTimes className="text-xs" />
                </button>
              ) : null}
            </div>

            <select
              className="rounded-xl bg-white border border-white text-[#1A1A18] px-3 py-2.5 outline-none shadow-sm focus:ring-2 focus:ring-white/70 cursor-pointer w-full sm:w-auto [&>option]:text-gray-800"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {statusOptions.map(({ value, label }) => (
                <option key={value || "all"} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-0.5">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#8A8A85]">
            {isLoading ? (
              <span>Loading redesigns…</span>
            ) : (
              <span>
                {redesignData.length} case
                {redesignData.length === 1 ? "" : "s"}
                {" · "}
                {totalAttempts} attempt
                {totalAttempts === 1 ? "" : "s"}
                {isSearching ? " matched" : " on this page"}
              </span>
            )}
            {isSearching ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200 px-2.5 py-0.5">
                Search shows up to 100 recent records
              </span>
            ) : null}
            {hasActiveFilters ? (
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1.5 text-[#2fa9bf] hover:text-[#2296aa] font-medium"
              >
                <FaTimes className="text-[10px]" />
                Clear filters
              </button>
            ) : null}
          </div>

          {!isSearching ? (
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={page <= 1 || isLoading}
                onClick={handlePrev}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Previous page"
              >
                <FaChevronLeft className="text-xs" />
              </button>
              <span className="text-xs text-[#4A4A48] tabular-nums min-w-[5.5rem] text-center">
                Page {totalPages === 0 ? 0 : page} of {totalPages || 1}
              </span>
              <button
                type="button"
                disabled={page >= (totalPages || 1) || isLoading}
                onClick={handleNext}
                className="inline-flex items-center justify-center h-8 w-8 rounded-lg border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-40 disabled:cursor-not-allowed transition"
                aria-label="Next page"
              >
                <FaChevronRight className="text-xs" />
              </button>
            </div>
          ) : null}
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-2xl border border-[#E8E8E4] shadow-sm">
          <table className="table-auto w-full">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85] w-14">
                  #
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Case ID
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Attempt
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Creation
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Reason / Category
                </th>
                <th className="py-3 px-5 text-left text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85]">
                  Status
                </th>
                {canAct ? (
                  <th className="py-3 px-5 text-center text-[11px] font-semibold tracking-wider uppercase text-[#8A8A85] w-28">
                    Action
                  </th>
                ) : null}
              </tr>
            </thead>

            <tbody>
              {error ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-red-500"
                  >
                    Couldn’t load redesigns. Please try again.
                  </td>
                </tr>
              ) : isLoading ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-[#8A8A85]"
                  >
                    <div className="inline-flex flex-col items-center gap-2">
                      <span
                        className="h-8 w-8 rounded-full border-2 border-[#E8E8E4] border-t-[#2fa9bf] animate-spin"
                        aria-hidden
                      />
                      Loading redesigns…
                    </div>
                  </td>
                </tr>
              ) : redesignData.length === 0 ? (
                <tr>
                  <td
                    colSpan={colCount}
                    className="py-14 text-center text-[#8A8A85]"
                  >
                    <div className="inline-flex flex-col items-center gap-2">
                      <FaInbox className="text-2xl text-[#DDDDD8]" />
                      <span>No redesign cases found</span>
                      {hasActiveFilters ? (
                        <button
                          type="button"
                          onClick={clearFilters}
                          className="text-[#2fa9bf] hover:underline"
                        >
                          Clear filters
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ) : (
                redesignData.map((caseItem, caseIndex) => (
                  <React.Fragment key={caseItem.case_id}>
                    {caseItem.redesigns.map((r, redesignIndex) => {
                      const idKey = `${caseItem.case_id}-${r.redesign_attempt}`;
                      const isCompleted =
                        r.status?.toLowerCase() === "completed";
                      const isUpdating = updating === idKey;
                      const isAltGroup = caseIndex % 2 === 1;

                      return (
                        <tr
                          key={idKey}
                          className={`border-b border-[#F0F0EC] last:border-0 transition-colors ${
                            isAltGroup
                              ? "bg-[#FAFAF8] hover:bg-[#F5F5F2]"
                              : "hover:bg-[#FAFAF8]"
                          }`}
                        >
                          {redesignIndex === 0 ? (
                            <td
                              rowSpan={caseItem.redesigns.length}
                              className="py-3.5 px-5 align-top text-[#8A8A85] tabular-nums"
                            >
                              {caseIndex + 1}
                            </td>
                          ) : null}

                          {redesignIndex === 0 ? (
                            <td
                              rowSpan={caseItem.redesigns.length}
                              className="py-3.5 px-5 align-top"
                            >
                              <a
                                href={`/dashboard/admin/cases/${caseItem.case_id}?tab=redesign`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-semibold text-[#2fa9bf] hover:text-[#2296aa] hover:underline break-all"
                              >
                                {caseItem.case_id}
                              </a>
                              {caseItem.redesigns.length > 1 ? (
                                <div className="mt-1 text-[11px] text-[#AAAAAA]">
                                  {caseItem.redesigns.length} attempts
                                </div>
                              ) : null}
                            </td>
                          ) : null}

                          <td className="py-3.5 px-5 align-middle">
                            <span className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-lg bg-[#F0F7F8] text-[#2fa9bf] text-xs font-semibold ring-1 ring-[#D4EBEF]">
                              {r.redesign_attempt || redesignIndex + 1}
                            </span>
                          </td>

                          <td className="py-3.5 px-5 align-middle text-[#4A4A48]">
                            {formatCreation(r.creation_time)}
                          </td>

                          <td className="py-3.5 px-5 align-middle break-words whitespace-normal max-w-[18rem] text-[#4A4A48]">
                            {r.category === "Others" ? (
                              <span className="italic text-[#6B6B66]">
                                {r.note || "—"}
                              </span>
                            ) : (
                              r.category || "—"
                            )}
                          </td>

                          <td className="py-3.5 px-5 align-middle">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${redesignStatusStyles(
                                r.status,
                              )}`}
                            >
                              {formatStatusLabel(r.status)}
                            </span>
                          </td>

                          {canAct ? (
                            <td className="py-3.5 px-5 align-middle text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  openConfirmModal(
                                    caseItem.case_id,
                                    r.redesign_attempt,
                                  )
                                }
                                disabled={isCompleted || isUpdating}
                                className={`inline-flex items-center justify-center gap-1.5 min-w-[5.5rem] px-3 py-1.5 rounded-xl text-xs font-medium transition ${
                                  isCompleted
                                    ? "bg-gray-100 text-gray-500 cursor-not-allowed ring-1 ring-gray-200"
                                    : isUpdating
                                      ? "bg-emerald-400 text-white cursor-wait"
                                      : "bg-emerald-500 hover:bg-emerald-600 text-white"
                                }`}
                              >
                                {isUpdating ? (
                                  "Saving…"
                                ) : isCompleted ? (
                                  <>
                                    <FaCheck className="text-[10px]" />
                                    Done
                                  </>
                                ) : (
                                  "Mark done"
                                )}
                              </button>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Bottom pagination */}
        {!isSearching && (totalPages || 0) > 1 ? (
          <div className="flex items-center justify-center gap-2 pb-1">
            <button
              type="button"
              disabled={page <= 1 || isLoading}
              onClick={handlePrev}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-40 disabled:cursor-not-allowed transition text-xs font-medium"
            >
              <FaChevronLeft className="text-[10px]" />
              Prev
            </button>
            <span className="text-xs text-[#8A8A85] tabular-nums px-2">
              {totalPages === 0 ? 0 : page} / {totalPages || 1}
            </span>
            <button
              type="button"
              disabled={page >= (totalPages || 1) || isLoading}
              onClick={handleNext}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] disabled:opacity-40 disabled:cursor-not-allowed transition text-xs font-medium"
            >
              Next
              <FaChevronRight className="text-[10px]" />
            </button>
          </div>
        ) : null}

        {/* Confirm Modal */}
        {confirmModal.visible ? (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-4 border border-[#E8E8E4]">
              <h2 className="text-base font-semibold text-[#4A4A48]">
                Confirm completion
              </h2>
              <p className="text-sm text-[#8A8A85] leading-relaxed">
                Mark redesign{" "}
                <span className="font-medium text-[#4A4A48]">
                  #{confirmModal.redesign_attempt}
                </span>{" "}
                on{" "}
                <span className="font-medium text-[#2fa9bf]">
                  {confirmModal.case_id}
                </span>{" "}
                as completed?
              </p>
              <div className="flex justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 rounded-xl border border-[#DDDDD8] bg-white text-[#4A4A48] hover:bg-[#F8F8F6] text-xs font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmDone}
                  className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium transition"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

export default Redesigns;
